import { useEffect, useState } from 'react';

/**
 * Whether this reader has asked for stillness.
 *
 * A HOOK BECAUSE THE ANSWER IS NEEDED IN RENDER rather than inside a loop: the
 * preference decides which URL an `<img>` gets, which is a render-time answer and has to
 * re-render when it changes.
 *
 * EVERY GIF SURFACE CALLS IT, AND THE PICKER USED TO BE THE ONLY ONE. While a GIF in a
 * letter was a poster until somebody pressed play there was no branch for a preference to
 * change, and this comment said so. Autoplay created the branch (docs/5 § D260805-2116),
 * so the picker's grid, a letter's attachment (`PostMedia`) and a draft's own preview
 * (`PostComposer`) all ask — and for the reader who asked for stillness the answer is the
 * poster and a play control, which is what everybody used to get.
 *
 * THE SILL STILL DOES NOT ASK, and that is not an inconsistency (docs/5 § D260805-2126):
 * the query is unreliable as a statement of intent — Windows sets it for anyone who
 * turned off window animations — and a bird that never moves is a broken surface rather
 * than a still one. A GIF degrades gracefully where the bird does not: somebody who has
 * the setting spuriously gets a poster with a play control, which is a picture they can
 * still see and still start. That difference in the FAILURE is the whole reason the two
 * surfaces answer differently.
 *
 * IT LISTENS RATHER THAN READING ONCE. The setting is toggled from outside the app
 * — Settings on iOS, the OS on desktop — and a value captured at mount would leave
 * a grid animating for somebody who just turned motion off, which is exactly the
 * person who would notice.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => query()?.matches ?? false);

  useEffect(() => {
    const mq = query();
    if (!mq) return;
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function query(): MediaQueryList | null {
  /* Absent in a non-browser render and in older WebViews. */
  return typeof matchMedia === 'function'
    ? matchMedia('(prefers-reduced-motion: reduce)')
    : null;
}
